import { useEffect, useState } from 'react';
import {
  Modal, ModalOverlay, ModalContent, ModalHeader, ModalBody, ModalFooter, ModalCloseButton,
  Button, Input, Select, Text, VStack, Alert, AlertIcon, Flex,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';

/** Rename a single value. The input is pre-filled with the current value. */
export function RenameValueModal({
  isOpen, onClose, currentValue, onSubmit, isPending,
}: {
  isOpen: boolean;
  onClose: () => void;
  currentValue: string;
  onSubmit: (newValue: string) => void;
  isPending: boolean;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState(currentValue);

  useEffect(() => { if (isOpen) setValue(currentValue); }, [isOpen, currentValue]);

  const trimmed = value.trim();
  const canSubmit = trimmed.length > 0 && trimmed !== currentValue && !isPending;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent data-testid="dictionary-rename-modal">
        <ModalHeader>{t('admin.dictionary.rename.title', { value: currentValue })}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && canSubmit) onSubmit(trimmed); }}
            autoFocus
            data-testid="dictionary-rename-input"
          />
          <Text fontSize="xs" color="gray.500" mt={2}>{t('admin.dictionary.rename.helper')}</Text>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose} isDisabled={isPending}>{t('common.cancel')}</Button>
          <Button
            colorScheme="brand"
            onClick={() => onSubmit(trimmed)}
            isDisabled={!canSubmit}
            isLoading={isPending}
            data-testid="dictionary-rename-submit"
          >
            {t('admin.dictionary.rename.confirm')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/** Merge a source value into one of the remaining values (the target). */
export function MergeValueModal({
  isOpen, onClose, source, options, onSubmit, isPending,
}: {
  isOpen: boolean;
  onClose: () => void;
  source: string;
  /** Candidate targets: { value, label }. The source is excluded by the caller. */
  options: { value: string; label: string }[];
  onSubmit: (target: string) => void;
  isPending: boolean;
}) {
  const { t } = useTranslation();
  const [target, setTarget] = useState('');

  useEffect(() => { if (isOpen) setTarget(''); }, [isOpen]);

  const canSubmit = target.length > 0 && !isPending;

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent data-testid="dictionary-merge-modal">
        <ModalHeader>{t('admin.dictionary.merge.title', { value: source })}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Text fontSize="sm" mb={2}>{t('admin.dictionary.merge.body', { value: source })}</Text>
          <Select
            placeholder={t('admin.dictionary.merge.selectTarget')}
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            data-testid="dictionary-merge-target"
          >
            {options.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </Select>
        </ModalBody>
        <ModalFooter gap={3}>
          <Button variant="ghost" onClick={onClose} isDisabled={isPending}>{t('common.cancel')}</Button>
          <Button
            colorScheme="brand"
            onClick={() => onSubmit(target)}
            isDisabled={!canSubmit}
            isLoading={isPending}
            data-testid="dictionary-merge-submit"
          >
            {t('admin.dictionary.merge.confirm')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

/**
 * Confirm a destructive delete. For free-text vocabularies it shows how many tracks are affected;
 * callers with their own copy (e.g. licenses, which are only deletable when unused) pass `message`.
 */
export function ConfirmDeleteModal({
  isOpen, onClose, value, trackCount, onConfirm, isPending, highImpact, message,
}: {
  isOpen: boolean;
  onClose: () => void;
  value: string;
  trackCount: number;
  onConfirm: () => void;
  isPending: boolean;
  highImpact?: boolean;
  /** Overrides the default "used by N tracks" body. */
  message?: string;
}) {
  const { t } = useTranslation();
  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent data-testid="dictionary-delete-modal">
        <ModalHeader>{t('admin.dictionary.delete.title', { value })}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack align="stretch" spacing={3}>
            {highImpact && (
              <Alert status="warning" borderRadius="md">
                <AlertIcon />
                <Text fontSize="sm">{t('admin.dictionary.delete.highImpact')}</Text>
              </Alert>
            )}
            <Text fontSize="sm" data-testid="dictionary-delete-affected">
              {message ?? t('admin.dictionary.delete.body', { value, count: trackCount })}
            </Text>
          </VStack>
        </ModalBody>
        <ModalFooter gap={3}>
          <Flex justify="flex-end" w="100%" gap={3}>
            <Button variant="ghost" onClick={onClose} isDisabled={isPending}>{t('common.cancel')}</Button>
            <Button
              colorScheme="red"
              onClick={onConfirm}
              isLoading={isPending}
              data-testid="dictionary-delete-confirm"
            >
              {t('admin.dictionary.delete.confirm')}
            </Button>
          </Flex>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
